import { useState, useEffect } from "react";
import tw from "tailwind-styled-components";
import { useMutation } from "react-query";
import axios from "axios";
import Config from "../../Config";
import Loading from "../../Components/Loading";
import { toast } from "react-toastify";
import {
  BoxTitle,
  BoxContainer,
  BoxHead,
  Underline,
  FilterTableWrapper,
  FilterWrapper,
} from "../../Components/Styles/PageStyles";
import { Form, Formik } from "formik";
import { useSelector } from "react-redux";
import NotFoundModel from "../../Components/NotFoundModel";
import AddBankDetails from "../../Components/BankDetails/AddBankDetails";
import DeleteBank from "../../Components/BankDetails/DeleteBank";
import Table from "../../Components/BankDetails/Table";
import SideModal from "../../Components/SideModal";
import LinkUnlink from "../../Components/BankDetails/LinkUnlink";

const BankDetails = () => {
  const user = useSelector((state) => state.UserReducer.user);
  const [addBankDetails, setAddBankDetails] = useState(false);
  const [isDeleteBank, setIsDeleteBank] = useState(false);
  const [apiData, setApiData] = useState([]);
  const [selectedData, setSelectedData] = useState(null);
  const [activeTab, setActiveTab] = useState("1");
  const [linkProperties, setLinkProperties] = useState(false);
  const propertyList = useSelector((state) => state.PropertyReducer.property);
  const fetchFunction = async () => {
    // isOnline = 1 for Online Settlement Bank, 0 for Offline Collection Bank
    const isOnline = activeTab === "1" ? 0 : 1;
    const url = `${Config.apiUrl}/bank/list?isOnline=${isOnline}`;

    const response = await axios.get(url, {
      headers: {
        authorization: "Bearer" + " " + user.token,
        "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
      },
    });
    return response.data;
  };

  const getSuccess = (res) => {
    if (res.isSuccess === true) {
      const bankAccountsOnly = res?.data?.filter((item) => item.type === 1) || [];
      setApiData(bankAccountsOnly);
    }
  };

  const getError = (res) => {
    toast.error("Failed to fetch bank details");
  };

  const {
    isLoading,
    mutate: getListMutate,
    error,
    isRefetching,
  } = useMutation(fetchFunction, {
    onSuccess: getSuccess,
    onError: getError,
  });

  // Fetch data when component mounts OR when activeTab changes
  useEffect(() => {
    getListMutate();
  }, [activeTab]); // Added activeTab as dependency

  //------- Add Bank Account -------
  const AddBankAccountFunction = async (values) => {
    return await axios.post(`${Config.apiUrl}/bank/add`, values, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${user.token}`,
      },
    });
  };

  const restorePaymentSuccess = (data) => {
    if (data?.data?.isSuccess === true) {
      toast.success(data?.data?.msg);
      setAddBankDetails(false);
      getListMutate();
    } else {
      toast.error(data?.data?.msg);
    }
  };

  const restorePaymentError = (data) => {
    toast.error(data?.response?.data?.msg || "An Error Occured");
  };

  const { isLoading: addBankAccountLoading, mutate: addBankAccountMutate } =
    useMutation(AddBankAccountFunction, {
      onSuccess: restorePaymentSuccess,
      onError: restorePaymentError,
    });

  const handleAddBankClose = () => {
    setAddBankDetails(false);
  };

  //------- Delete Bank -------
  const deleteBankFunction = async (values) => {
    return await axios.post(`${Config.apiUrl}/bank/remove`, values, {
      headers: {
        authorization: "Bearer" + " " + user.token,
        "Content-Type": "application/json",
      },
    });
  };

  const onDeleteBankSuccess = (data) => {
    if (data?.data?.isSuccess === true) {
      toast.success(data?.data?.msg || "Bank account deleted successfully");
      getListMutate();
      setIsDeleteBank(false);
    } else {
      toast.error(data?.data?.msg || "An Error Occured");
    }
  };

  const onDeleteBankError = (data) => {
    toast.error(data?.response?.data?.msg || "An Error Occured");
  };

  const { isLoading: deleteBankLoading, mutate: deleteBankMutate } =
    useMutation(deleteBankFunction, {
      onSuccess: onDeleteBankSuccess,
      onError: onDeleteBankError,
    });

  const handleDeleteModalClose = () => {
    setIsDeleteBank(false);
  };

  // Handle tab change
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
  };

  //------- Link Property Staff -------
  const linkPropertyFunction = async (values) => {
    return await axios.post(`${Config.apiUrl}/bank/settlement/change`, values, {
      headers: {
        authorization: "Bearer" + " " + user.token,
        "Content-Type": "application/json",
      },
    });
  };

  const onLinkPropertyeSuccess = (data) => {
    if (data?.data?.isSuccess === true) {
      toast.success(data?.data?.msg || "Success");
    } else {
      toast.error(data?.rdata?.msg || "An Error Occured");
    }
  };

  const onLinkPropertyError = (data) => {
    toast.error(data?.response?.data?.msg || "An Error Occured");
  };

  const {
    isLoading: linkPropertyLoading,
    mutate: linkPropertyMutate,
  } = useMutation(linkPropertyFunction, {
    onSuccess: onLinkPropertyeSuccess,
    onError: onLinkPropertyError,
  });

  return (
    <BoxContainer>
      <BoxHead>
        <BoxTitle className="!mb-0 whitespace-nowrap">
          Bank Accounts
          <Underline />
        </BoxTitle>
        {activeTab === '1' &&
          <Filters setAddBankDetails={setAddBankDetails} />
        }
      </BoxHead>
      <FloorTabs>
        <FloorTab
          onClick={() => handleTabChange('1')}
          $active={activeTab === "1"}
        >
          Offline Collection Bank
        </FloorTab>
        <FloorTab
          onClick={() => handleTabChange('2')}
          $active={activeTab === "2"}
        >
          Online Settlement Bank
        </FloorTab>
      </FloorTabs>
      <SideModal
        isOpen={linkProperties}
        onClose={() => setLinkProperties(false)}
      >
        <LinkUnlink
          setLinkProperties={setLinkProperties}
          linkProperties={linkProperties}
          linkPropertyLoading={linkPropertyLoading}
          linkPropertyMutate={linkPropertyMutate}
          property={propertyList}
          selectedData={selectedData}
        />
      </SideModal>

      <FilterTableWrapper>
        <FilterWrapper>
          <div className="flex items-center">
            <Text>
              Total records:{" "}
              <span className="font-semibold">
                {apiData?.length || 0}
              </span>
            </Text>
          </div>
        </FilterWrapper>
        {!error && !isLoading && (
          <TableWrapper>
            <Table
              apiData={error ? [] : apiData}
              setSelectedData={setSelectedData}
              setIsDeleteBank={setIsDeleteBank}
              activeTab={activeTab}
              setLinkProperties={setLinkProperties}
            />
            {error && !isLoading && <NotFoundModel />}
          </TableWrapper>
        )}
      </FilterTableWrapper>

      {(isLoading || isRefetching) && <Loading />}

      <SideModal
        isOpen={addBankDetails}
        onClose={() => setAddBankDetails(false)}
      >
        <AddBankDetails
          handleClose={handleAddBankClose}
          addBankAccountLoading={addBankAccountLoading}
          addBankAccountMutate={addBankAccountMutate}
        />
      </SideModal>
      {/* </div>
        </>
      )} */}

      {isDeleteBank && (
        <DeleteBank
          deleteBankMutate={deleteBankMutate}
          deleteBankLoading={deleteBankLoading}
          setIsDeleteBank={setIsDeleteBank}
          selectedData={selectedData}
          handleClose={handleDeleteModalClose}
        />
      )}
    </BoxContainer>
  );
};

const Filters = ({ setAddBankDetails }) => {
  return (
    <Formik>
      <Form>
        <FilterContainer>
          <AddBtn
            type="button"
            onClick={() => {
              setAddBankDetails(true);
            }}
          >
            Add Bank
          </AddBtn>
        </FilterContainer>
      </Form>
    </Formik>
  );
};

const AddBtn = tw.button`border whitespace-nowrap border-gray-200 flex items-center justify-center text-white h-full rounded-md px-4 cursor-pointer bg-gradient-to-r from-primary-color to-[#00A0FF] hover:brightness-110 py-2.5 text-sm transition-colors duration-200`;
const FilterContainer = tw.div`grid grid-cols-2 w-full gap-4 md:flex md:space-x-4 items-end mt-0 md:mt-0`;
const TableWrapper = tw.div`mt-4 bg-white rounded-md`;
const FloorTabs = tw.div`flex overflow-x-auto bg-white rounded-md my-5 border-b`;
const FloorTab = tw.button`
  ${(p) =>
    p.$active
      ? " text-primary-color border-b-2 border-b-primary-color bg-cyan-50 "
      : " text-gray-700 "} 
   px-5 py-3 cursor-pointer font-medium transition-all duration-200 whitespace-nowrap text-sm
`;

const Text = tw.p`text-gray-500 text-sm font-normal`;

export default BankDetails;